"use client";

import React, { useEffect, useState } from "react";
import {
  Calendar,
  Check,
  ChevronDown,
  Clock,
  CreditCard,
  FileCode2,
  FingerprintIcon,
  Mail,
  Package,
  ShoppingCart,
  Ticket,
  User,
  VideoIcon,
  Brain,
} from "lucide-react";
import { format } from "date-fns";
import _ from "lodash";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { type HttpError, useShow } from "@refinedev/core";
import { z } from "zod";
import { CopyButton } from "@/components/common/copy-button";
import { getStatusColor } from "@/pages/orders/utils";
import { TicketsDialog } from "@/pages/orders/components/tickets-dialog";
import { FormSchema } from "@/pages/orders/schemas/dynamic-form-data";
import AutoForm from "@/components/ui/auto-form";
import JsonEditor from "@/components/fields/json-editor";
import { useDocumentTitle } from "@refinedev/react-router";

const productSchema = z.object({
  id: z.string(),
  name: z.string(),
  price: z.number(),
  quantity: z.number(),
});

// LogEntry schema
const logEntrySchema = z.object({
  action: z.string(),
  actor: z.string(),
  timestamp: z.string(),
  message: z.string(),
  error: z
    .object({
      name: z.string(),
      message: z.string(),
      stack: z.string().optional(),
    })
    .nullish(),
});

// OrderMetadata schema
const orderMetadataSchema = z.object({
  expiresAt: z.string(),
  logs: z.array(logEntrySchema),
  error: z.any(),
  serviceFee: z.number().nullable().optional(),
  user: z.any(),
  event: z.any(),
});

// OrderParams schema
const orderParamsSchema = z.object({
  eventId: z.string(),
  email: z.string(),
  products: z.array(productSchema),
  discountCode: z.string().nullish(),
});

// OrderData schema (the main schema)
const orderDataSchema = z.object({
  id: z.string(),
  confirmationNumber: z.string(),
  botStatus: z.string(),
  status: z.string(),
  metadata: orderMetadataSchema,
  params: orderParamsSchema,
  paymentMethod: z.any(),
  amount: z.number(),
  discount: z.number().nullish(),
  ticketing: z.any(),
  reservationInfo: z.any(),
  purchaseInfo: z.any(),
  tickets: z.array(z.any()).nullish(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string(),
  updatedBy: z.string(),
});

// Type inference
type OrderData = z.infer<typeof orderDataSchema>;

export function OrdersShow() {
  const {
    query: { data: queryResult },
    showId,
  } = useShow<OrderData, HttpError>();
  const [orderData, setData] = useState<OrderData | undefined>();
  const [isPaymentOpen, setIsPaymentOpen] = useState<boolean>(false);
  const setTitle = useDocumentTitle();

  useEffect(() => {
    if (queryResult) {
      const data = orderDataSchema.parse(queryResult);
      setData(data);
      setTitle("Order #" + data.confirmationNumber);
    }
  }, [queryResult]);

  const dynamicFormSchema = React.useMemo(() => {
    const dynamicForm = FormSchema.safeParse(orderData?.ticketing?.form);
    const dynamicFormFields = dynamicForm.data?.fields;
    const selectedGroup = orderData?.ticketing?.groups?.find((g: any) =>
      g.ticketIds?.some((ticketId: string) =>
        orderData.params.products.some((product) => product.id === ticketId),
      ),
    );

    if (!dynamicFormFields) {
      return null;
    }
    const allReferenceIds: string[] = dynamicFormFields
      .map((field) => field.referenceId)
      .filter((id): id is string => typeof id === "string" && !!id);

    // find all unique referenceIds from dynamicFormFields array
    const uniqueReferenceIds: string[] = [...new Set<string>(allReferenceIds)];

    const schemaObject: Record<string, z.ZodType<any, any>> = {};
    dynamicFormFields.forEach((field) => {
      let fieldSchema: z.ZodTypeAny;
      switch (field.type) {
        case "text":
        case "textarea":
        case "phonenumber":
        case "time":
          fieldSchema = z.string().default("").optional();
          break;
        case "email":
          fieldSchema = z.string().email("Invalid email address").optional();
          break;
        case "number":
          fieldSchema = z.number().optional().nullable();
          break;
        case "checkbox":
          fieldSchema = z.boolean().default(false);
          break;
        case "select":
          fieldSchema = z.string().optional();
          break;
        case "multiselect":
          fieldSchema = z.array(z.string()).default([]);
          break;
        case "date":
          fieldSchema = z
            .string()
            .optional()
            .refine((val) => {
              if (val) {
                const date = new Date(val);
                return !isNaN(date.getTime());
              }
              return true;
            });
          break;
        default:
          fieldSchema = z.string().optional();
      }

      // Make it required if needed
      if (field.required) {
        if (
          field.type === "text" ||
          field.type === "textarea" ||
          field.type === "phonenumber" ||
          field.type === "select" ||
          field.type === "time"
        ) {
          fieldSchema = z.string().nonempty(`${field.label} is required`);
        }
        if (field.type === "email") {
          fieldSchema = z
            .string()
            .email("Invalid email address")
            .nonempty(`${field.label} is required`);
        }
        if (field.type === "number") {
          fieldSchema = z.number();
        }
        if (field.type === "date") {
          fieldSchema = z.string().refine((val) => {
            if (val) {
              const parsedDate = new Date(val);

              if (isNaN(parsedDate.getTime())) {
                return false;
              }

              const year = parsedDate.getFullYear();
              const currentYear = new Date().getFullYear();

              return year >= 1900 && year <= currentYear;
            }
            return true;
          });
        }
        if (field.type === "multiselect") {
          fieldSchema = z
            .array(z.string())
            .nonempty("At least one option must be selected");
        }
      }
      fieldSchema = fieldSchema.describe(field.label);
      if (field.referenceId) {
        if (!schemaObject[field.referenceId]) {
          schemaObject[field.referenceId] = z.object({});
        }
        // To add fields to the object, we need to reconstruct it with .extend()
        schemaObject[field.referenceId] = (
          schemaObject[field.referenceId] as z.ZodObject<any>
        ).extend({
          [field.id]: fieldSchema,
        });
      } else {
        schemaObject[field.id] = fieldSchema;
      }
    });

    uniqueReferenceIds.forEach((referenceId: string) => {
      const correspondingProduct = orderData?.params?.products?.find(
        ({ id }) => id === referenceId,
      );
      const correspondingGroup = orderData?.ticketing?.groups?.find(
        ({ id }: any) => id === referenceId && selectedGroup.id === id,
      );

      if (!correspondingProduct && !correspondingGroup) {
        delete schemaObject[referenceId];
        return;
      }

      if (correspondingProduct) {
        schemaObject[referenceId] = z
          .array(schemaObject[referenceId])
          .describe(correspondingProduct?.name);
      }
      if (correspondingGroup) {
        schemaObject[referenceId] = z
          .array(schemaObject[referenceId])
          .describe(correspondingGroup.name);
      }
    });

    const formFieldConfig: any = {};

    Object.keys(orderData?.ticketing?.form?.values ?? {}).forEach((k) => {
      formFieldConfig[k] = {
        inputProps: {
          disabled: true,
        },
      };
      if (_.isArray(orderData?.ticketing?.form?.values[k])) {
        Object.keys(orderData?.ticketing?.form?.values[k]?.[0]).forEach(
          (nestedK) => {
            formFieldConfig[k][nestedK] = {
              inputProps: {
                disabled: true,
              },
            };
          },
        );
      }
    });

    return { schema: z.object(schemaObject), config: formFieldConfig };
  }, [orderData?.ticketing?.form]);

  // Format dates for display
  const formatDate = (dateString: string | null): string => {
    try {
      return dateString ? format(new Date(dateString), "PPP 'at' p") : "N/A";
    } catch (error) {
      return dateString || "N/A";
    }
  };

  // Format currency
  const formatCurrency = (amount: number, currency = "EUR"): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  // Format milliseconds to readable time
  const formatTime = (ms: number): string => {
    if (ms < 1000) {
      return `${ms}ms`;
    } else if (ms < 60000) {
      return `${(ms / 1000).toFixed(2)}s`;
    } else {
      return `${(ms / 60000).toFixed(2)}m`;
    }
  };

  // Format large numbers
  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat("en-US").format(num);
  };

  if (!orderData) {
    return <div>Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-6 max-w-7xl">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 px-6 py- rounded-lg shadow">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Order Details</h1>
          <p className="text-muted-foreground">
            View detailed information about this order
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={getStatusColor(orderData.status)}>
            {orderData.status}
          </Badge>
          <Badge
            variant={
              orderData.botStatus.includes("Error") ? "destructive" : "outline"
            }
            className={getStatusColor(orderData.botStatus)}
          >
            {orderData.botStatus}
          </Badge>
        </div>
      </div>
      <div className="flex flex-row gap-2 items-center">
        <Button
          variant="default"
          onClick={() =>
            window.open(
              `https://console.cloud.google.com/logs/query;query=${encodeURIComponent(
                `jsonPayload.correlationId=${orderData.id}`,
              )};startTime=${new Date(
                orderData.createdAt,
              ).toISOString()};project=${
                import.meta.env.VITE_ENV === "dev"
                  ? "moodfordev"
                  : "wheretogo-e5f51"
              }`,
              "_blank",
            )
          }
        >
          <FileCode2 className="w-5 h-5 mr-2" />
          Logs
        </Button>
        <Button
          variant="default"
          onClick={() =>
            window.open(
              `https://sheero-oq.sentry.io/issues/?project=4509434506182656&query=${encodeURIComponent(
                `is:unresolved issue.priority:[high, medium] correlationId:${orderData.id}`,
              )}&referrer=issue-list&statsPeriod=14d`,
              "_blank",
            )
          }
        >
          <img
            src="https://images.icon-icons.com/2148/PNG/512/sentry_icon_132005.png"
            className="w-5 h-5 mr-2"
          />
          Errors
        </Button>
        <Button
          variant="default"
          disabled={!orderData.reservationInfo?.session}
          onClick={() =>
            window.open(
              `https://browserbase.com/sessions/${orderData.reservationInfo?.session?.id}`,
              "_blank",
            )
          }
        >
          <VideoIcon className="mr-2 w-5 h-5" />
          Reservation
        </Button>
        <Button
          variant="default"
          disabled={!orderData.purchaseInfo?.session}
          onClick={() =>
            window.open(
              `https://browserbase.com/sessions/${orderData.purchaseInfo?.session?.id}`,
              "_blank",
            )
          }
        >
          <VideoIcon className="mr-2 w-5 h-5" />
          Purchase
        </Button>

        <div className="flex flex-1" />
        {["PurchaseError", "PurchaseSucceeded", "ManualPurchase"].includes(
          orderData.botStatus,
        ) && <TicketsDialog order={orderData} />}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Order Summary */}
        <Card className="md:col-span-2 p-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Order Summary
            </CardTitle>
            <CardDescription>See information about the order</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1 px-4 py-2">
                <p className="text-sm font-medium text-muted-foreground">
                  Confirmation No.
                </p>
                <div className="flex items-center gap-2">
                  <p className="font-mono">{orderData.confirmationNumber}</p>
                  <CopyButton
                    text={orderData.confirmationNumber}
                    message="Confirmation number copied to clipboard"
                  />
                </div>
              </div>
              <div className="space-y-1 px-4 py-2">
                <p className="text-sm font-medium text-muted-foreground">
                  Order ID
                </p>
                <div className="flex items-center gap-2">
                  <p className="font-mono">{orderData.id}</p>
                  <CopyButton
                    text={orderData.id}
                    message="Order ID copied to clipboard"
                  />
                </div>
              </div>
              {/** Meta Items with consistent padding */}
              {["Created At", "Updated At", "Expires At", "Created By"].map(
                (label, idx) => (
                  <div key={idx} className="space-y-1 px-4 py-2">
                    <p className="text-sm font-medium text-muted-foreground">
                      {label}
                    </p>
                    <p className="flex items-center gap-2">
                      {label.includes("Expires") ? (
                        <Clock className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                      )}
                      {formatDate(
                        label === "Created At"
                          ? orderData.createdAt
                          : label === "Updated At"
                          ? orderData.updatedAt
                          : label === "Expires At"
                          ? orderData.metadata.expiresAt
                          : orderData.createdBy,
                      )}
                    </p>
                  </div>
                ),
              )}
              {orderData.params.discountCode && (
                <div className="space-y-1 px-4 py-2">
                  <p className="text-sm font-medium text-muted-foreground">
                    Discount Code
                  </p>
                  <div className="flex items-center gap-2">
                    <p className="font-mono">{orderData.params.discountCode}</p>
                    <CopyButton
                      text={orderData.params.discountCode}
                      message="Discount code copied to clipboard"
                    />
                  </div>
                </div>
              )}
            </div>

            <Separator />

            <div className="space-y-4">
              <h3 className="font-medium px-4 py-2">Products</h3>
              <div className="bg-muted rounded-lg p-6">
                <div className="grid grid-cols-5 gap-4 font-medium text-sm text-muted-foreground mb-2">
                  <div>Product ID</div>
                  <div>Name</div>
                  <div>Quantity</div>
                  <div>Price</div>
                  <div>Total</div>
                </div>
                {orderData.params.products.map((product) => (
                  <div
                    key={product.id}
                    className="grid grid-cols-5 gap-4 py-3 border-t border-border items-center"
                  >
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      {product.id}
                    </div>
                    <div className="flex items-center gap-2">
                      {product.name}
                    </div>
                    <div>{product.quantity}</div>
                    <div>{formatCurrency(product.price)}</div>
                    <div className="font-medium">
                      {formatCurrency(product.quantity * product.price)}
                    </div>
                  </div>
                ))}
                <div className="grid grid-cols-5 gap-4 py-3 border-t border-border">
                  <div className="col-span-4 text-right font-medium">
                    Service Fee:
                  </div>
                  <div>
                    {formatCurrency(orderData.metadata.serviceFee ?? 0)}
                  </div>
                </div>
                {orderData.discount && (
                  <div className="grid grid-cols-5 gap-4 py-3 border-t border-border">
                    <div className="col-span-4 text-right font-medium">
                      Discount:
                    </div>
                    <div>
                      -
                      {formatCurrency(
                        orderData.discount * (orderData.amount / 100),
                      )}{" "}
                      ({orderData.discount * 100}%)
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-5 gap-4 py-3 border-t border-border">
                  <div className="col-span-4 text-right font-medium">
                    Total Amount:
                  </div>
                  <div className="font-bold">
                    {formatCurrency(
                      (orderData.discount
                        ? (1 - orderData.discount) * orderData.amount
                        : orderData.amount) /
                        100 +
                        (orderData.metadata.serviceFee ?? 0),
                    )}
                  </div>
                </div>
              </div>
            </div>
            {dynamicFormSchema && (
              <div className="space-y-4 ">
                <h3 className="font-medium px-4 py-2">Form</h3>
                <div className="rounded-lg p-6">
                  <AutoForm
                    formSchema={dynamicFormSchema.schema}
                    values={orderData?.ticketing?.form?.values}
                    fieldConfig={dynamicFormSchema.config}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Customer Information */}
        <Card className="p-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Customer Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 px-4 py-2">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">
                User ID
              </p>
              <div className="flex items-center gap-2 font-mono text-sm">
                <FingerprintIcon className="h-4 w-4 text-muted-foreground" />
                {orderData.createdBy}
                <CopyButton
                  text={orderData.createdBy}
                  message="User ID copied"
                />
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Email</p>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                {orderData.params.email}
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">
                Username
              </p>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                {orderData.metadata.user.displayName}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <Button
                type="button"
                variant="default"
                onClick={() =>
                  window.open(
                    `https://console.firebase.google.com/u/0/project/${
                      import.meta.env.VITE_ENV === "dev"
                        ? "moodfordev"
                        : "wheretogo-e5f51"
                    }/firestore/databases/-default-/data/users/${
                      orderData.createdBy
                    }/orders/${orderData.id}`,
                    "_blank",
                  )
                }
              >
                <img
                  className="w-6"
                  src="https://www.gstatic.com/mobilesdk/240501_mobilesdk/firebase_28dp.png"
                />
                Firebase
              </Button>
              <Button
                type="button"
                variant="default"
                onClick={() =>
                  window.open(
                    `https://app.amplitude.com/analytics/musicofourdesire/users?searchType=search&search=${orderData.createdBy}&property=gp:user_id`,
                    "_blank",
                  )
                }
              >
                <img
                  className="w-6"
                  src="https://seeklogo.com/images/A/amplitude-logo-67DFD43C34-seeklogo.com.png"
                />
                Amplitude
              </Button>
              <Button
                type="button"
                variant="default"
                onClick={() =>
                  window.open(
                    `https://dashboard.stripe.com/test/customers/${orderData.metadata.user.externalIds.stripe}`,
                    "_blank",
                  )
                }
              >
                <img
                  className="w-6"
                  src="https://logos-world.net/wp-content/uploads/2021/03/Stripe-Emblem.png"
                />
                Stripe
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Timeline */}
        <Card className="md:col-span-2 p-6">
          <CardHeader>
            <CardTitle className="flex flex-row justify-between">
              <div className="flex ites-center gap-2">
                <Clock className="h-5 w-5" />
                Activity Timeline
              </div>
            </CardTitle>
            <CardDescription>
              History of actions and events for this order
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 py-2">
            <div className="relative pl-6 space-y-6 border-l border-l-ring">
              {orderData.metadata.logs.map((log, index) => (
                <div key={index} className="relative">
                  <div className="absolute -left-[37px] p-1 rounded-full bg-background border border-border">
                    {log.actor === "user" ? (
                      <User className="h-4 w-4 text-primary" />
                    ) : (
                      <Check className="h-4 w-4 text-green-500" />
                    )}
                  </div>
                  <div className="space-y-1 px-4 py-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{log.action}</span>
                      <Badge variant="outline" className="text-xs">
                        {log.actor}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {log.message}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(log.timestamp)}
                    </p>
                  </div>
                  {log.error && (
                    <div className="space-y-1 px-4 py-2 ">
                      <span className="font-medium text-red-500">
                        {log.error.name}
                      </span>
                      <div className="text-red-400">{log.error.message}</div>
                      <p className="text-sm text-muted-foreground">
                        {log.error.stack}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Ticketing Information */}
        <Card className="p-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5" />
              Ticketing Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 px-4 py-2">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Event</p>
              <a
                href={`/events/edit/${orderData.params.eventId}`}
                target="_blank"
                className="text-blue-500 hover:underline truncate"
                rel="noreferrer"
              >
                {orderData.metadata.event.name}
              </a>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">
                Provider
              </p>
              <p>{orderData.ticketing.provider}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">
                Created At
              </p>
              <p>{formatDate(orderData.ticketing.createdAt)}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">
                Tickets URL
              </p>
              <a
                href={orderData.ticketing.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline break-all"
              >
                {orderData.ticketing.url}
              </a>
            </div>
            <div className="flex flex-col items-start justify-center space-y-1 space-x-1">
              <JsonEditor
                text="Reservation Info"
                json={orderData.reservationInfo}
                onChanged={(_) => {}}
              />
              <JsonEditor
                text="Purchase Info"
                json={orderData.purchaseInfo}
                onChanged={(_) => {}}
              />
            </div>
          </CardContent>
        </Card>

        {/* AI Metrics Card */}
        {orderData.purchaseInfo?.aiMetrics && (
          <Card className="p-6 md: col-span-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5" />
                AI Metrics
              </CardTitle>
              <CardDescription>
                Performance metrics from AI processing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 px-4 py-2">
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">
                    Estimated Cost
                  </p>
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                    <p className="font-mono text-lg">
                      {formatCurrency(
                        orderData.purchaseInfo.aiMetrics.estimatedCost,
                        "USD",
                      )}
                    </p>
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">
                    Inference Time
                  </p>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <p className="font-mono text-lg">
                      {formatTime(
                        orderData.purchaseInfo.aiMetrics.inferenceTimeMs,
                      )}
                    </p>
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">
                    Tokens Used
                  </p>
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <p className="font-mono text-lg">
                      {formatNumber(
                        orderData.purchaseInfo.aiMetrics.tokensUsed,
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Details */}
        <Card className="md:col-span-2 p-6 hidden">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Payment Details
            </CardTitle>
            <CardDescription>
              {/* Payment intent ID: {orderData.paymentIntent.id} */}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 py-2">
            <Collapsible
              open={isPaymentOpen}
              onOpenChange={setIsPaymentOpen}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 px-4 py-2">
                {/* Summary fields */}
              </div>
              <CollapsibleTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-1 px-4 py-2"
                >
                  {isPaymentOpen ? "Show less" : "Show more details"}
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${
                      isPaymentOpen ? "rotate-180" : ""
                    }`}
                  />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 px-4 py-2 space-x-2">
                {/* Detailed payment sections with consistent p-2 or p-4 as needed */}
              </CollapsibleContent>
            </Collapsible>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
